/**
 * The talk's player and the pieces the guest's pages share: the player their page's talk sheet (`page/Sheets.tsx`)
 * and a room's replay sheet (`views/rooms/ReplaySheet.tsx`) play a talk in, its chapter list and a moment arriving on
 * it, the arrivals and answer shapes the room and the live use, and the small words they share.
 */
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  HandoutConfig,
  OfferJson,
  PollConfig,
  QuestionConfig,
  ReactionConfig,
  WatchCaption,
  WatchChapter,
  WatchInteraction,
  WatchSession,
} from "../../shared/types";
import { offerArrived, offerClosed } from "../../shared/offer";
import { assetUrl } from "../lib/api";
import { EndCard, OfferCard, OfferTab } from "./OfferInPlayer";
import { IcPause, IcPlay } from "./page/icons";
import { type ReplayReactions, useReplayReactions } from "./ReplayReactions";
import { captionAt } from "./room/talk";
import "../styles/watch.css";

// ── small helpers ───────────────────────────────────────────────────────
function mmss(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Has the offer landed for this viewer?
 *
 * The talk's own position is the clock: the player reports it (`onProgress` →
 * `watched_sec`) on every seek, pause and few seconds of playing, and it is what
 * a returning viewer comes back with. Once the offer is up it stays up, so
 * scrubbing back does not take it away again.
 */
export function useOfferArrived(offer: OfferJson | null, watchedSec: number): boolean {
  const [seen, setSeen] = useState(false);
  const here = offer !== null && offerArrived(offer, watchedSec);
  useEffect(() => {
    if (here) setSeen(true);
  }, [here]);
  return here || seen;
}

/** Which chapter the playhead is inside, or -1 before the first one. */
export function currentChapter(chapters: readonly WatchChapter[], seconds: number): number {
  let index = -1;
  for (let i = 0; i < chapters.length; i += 1) {
    if (chapters[i].t <= seconds) index = i;
  }
  return index;
}
export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}
function initials(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  const two = w.length >= 2 ? w[0][0] + w[1][0] : (w[0] ?? "").slice(0, 2);
  return two.toUpperCase() || "FW";
}
/** A first name that reads as one, however it was typed into the form. */
export function properName(full: string): string {
  const one = firstName(full);
  return one ? one[0].toUpperCase() + one.slice(1) : one;
}
/** What a moment is called, wherever it is listed rather than shown. */
function momentTitle(interaction: WatchInteraction): string {
  const cfg = interaction.config;
  if (interaction.type === "poll") return (cfg as PollConfig).question;
  if (interaction.type === "question") return (cfg as QuestionConfig).question;
  if (interaction.type === "reaction") return (cfg as ReactionConfig).prompt;
  return (cfg as HandoutConfig).title;
}

// ── inline icons (stroke unless noted) ──────────────────────────────────
type IcProps = { className?: string };
// A default 1em size keeps any icon without a dedicated CSS size sane; a scoped
// `.w-* svg{ width:.. }` rule (watch.css) overrides it where a bigger size is wanted.
const S = (d: ReactNode, extra?: Record<string, unknown>) => (p: IcProps) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={p.className} {...extra}>
    {d}
  </svg>
);
const IcPlayF = (p: IcProps) => (<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M7 5.5v13l11-6.5z" /></svg>);
const IcCheck = S(<path d="M4 12l5 5L20 6" />, { strokeWidth: 2.2 });
const IcShield = S(<><path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6Z" /><path d="M9 12l2 2 4-4" /></>);
const IcVol = S(<><path d="M11 5 6 9H3v6h3l5 4V5Z" /><path d="M16 9a4 4 0 0 1 0 6" /></>, { strokeWidth: 1.7 });
const IcMute = S(<><path d="M11 5 6 9H3v6h3l5 4V5Z" /><path d="m16 10 4 4M20 10l-4 4" /></>, { strokeWidth: 1.7 });
const IcExpand = S(<path d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />, { strokeWidth: 1.7 });
const IcList = S(<path d="M4 7h16M4 12h10M4 17h13" />, { strokeWidth: 2.1 });
const IcPauseF = (p: IcProps) => (<svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>);

// ── the player ───────────────────────────────────────────────────────────
// The real HLS player when the recording is ready (`playback_url`), else the
// branded placeholder. Both report the watch position up (P3 → /progress).
/** What the player needs from the moment scheduler, and how it answers back. */
export type Arrivals = {
  /** The moment on screen, or null when the player is clear. */
  up: WatchInteraction | null;
  /** How many are queued behind it (the "1 waiting" pill). */
  waiting: number;
  /** Does the one on screen hold the talk? */
  paused: boolean;
  /**
   * The playhead moved. `wall` is the seconds of real time since the last tick,
   * which is what separates a slow frame from a seek.
   */
  onTime: (tick: { from: number; to: number; playing: boolean; wall: number; rate: number }) => void;
  /** Later, Escape, or the close control. */
  dismiss: (id: string) => void;
};

/**
 * Which frame the talk sits in. "room" is the room's own: the replay chips, the
 * lower third, the saved-place caption. "watch" is the Watch page's: the picture,
 * one small chapters icon at the top right, the moments as dots on its bar, the
 * offer in the picture, and one bar that fades while it plays. "sheet" is their
 * page's talk sheet, as the prototype draws it (their-page.css `.player`): the
 * picture, a tap to play or pause, the words being said while it plays, the time
 * and the length, and the bar with a tick where each chapter starts; a moment
 * rises under it, and the offer and chapters sit in the sheet.
 */
type PlayerChrome = "room" | "watch" | "sheet";

/** The way in for whatever sits outside the picture: a moment chip, the theatre. */
export interface PlayerHandle {
  jumpTo: (seconds: number) => void;
  pause: () => void;
}

type PlayerProps = {
  session: WatchSession;
  onProgress: (sec: number, pct: number) => void;
  arrivals: Arrivals;
  onAnswer: AnswerFn;
  chrome?: PlayerChrome;
  /** Filled in while the player is on screen, so the page can drive it. */
  player?: RefObject<PlayerHandle | null>;
  /** The viewer's address, carried on the offer's link so a signup is matched. */
  viewerEmail?: string;
  /** The press on the offer that counts, wherever in the picture it happens. */
  onConvert?: () => void;
  /** The offer's video started, or played to the end. */
  onOfferVideo?: (state: "play" | "end") => void;
  /** A live's replay: where people reacted, drawn on the bar, and the five to react with. */
  reactions?: ReplayReactions;
  /** The talk's words, one line on the picture while it plays (the sheet). */
  captions?: readonly WatchCaption[];
  /** Play as soon as it can: the press that opened the sheet asked for the talk. */
  autoPlay?: boolean;
  /** The talk started or stopped, for the sheet's own button under the picture. */
  onPlayState?: (playing: boolean) => void;
};

export function Player(props: PlayerProps): ReactNode {
  return props.session.playback_url ? <StreamPlayer {...props} /> : <PlaceholderPlayer {...props} />;
}

/** How long the Watch page's bar waits before it gets out of the way. */
const IDLE_MS = 2600;

/**
 * The bar under a finger or a mouse: pressed, it follows across and seeks
 * where it is let go, so a tap is a short drag. A moment's dot on the bar
 * keeps its own press. `onSeek` gets a share of the talk, 0 to 1.
 */
function useScrubDrag(onSeek: (frac: number) => void) {
  const [drag, setDrag] = useState<number | null>(null);
  const held = useRef(false);
  const fracOf = (e: ReactPointerEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return r.width > 0 ? Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) : 0;
  };
  const handlers = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest(".w-mdot")) return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      held.current = true;
      setDrag(fracOf(e));
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      if (held.current) setDrag(fracOf(e));
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      if (!held.current) return;
      held.current = false;
      setDrag(null);
      onSeek(fracOf(e));
    },
    onPointerCancel: () => {
      held.current = false;
      setDrag(null);
    },
  };
  return { drag, handlers };
}

/**
 * The real player: a native `<video>` fed the HLS manifest. Safari plays HLS
 * natively; elsewhere hls.js is lazy-loaded (a code-split chunk, so it never
 * touches the placeholder path or the initial bundle). Custom Ledger controls
 * drive the element; the time signal is throttled to a ~5s POST cadence (plus a
 * flush on pause / ended / seek), which is what updates watched_pct / attended
 * / completed server-side.
 */
const PROGRESS_POST_EVERY_SEC = 5;

/** How long an answer to a quick question may be. */
const ANSWER_MAX = 200;

/** The speeds the talk can play at, in the order the button walks them. */
export const RATES: readonly number[] = [1, 1.25, 1.5, 1.75, 2];
const RATE_KEY = "fw.watch.rate";

function nextRate(rate: number): number {
  const at = RATES.indexOf(rate);
  return RATES[(at + 1) % RATES.length] ?? 1;
}

function rateLabel(rate: number): string {
  return `${Number.isInteger(rate) ? rate : rate.toString()}×`;
}

function savedRate(): number {
  try {
    const kept = Number(localStorage.getItem(RATE_KEY));
    return RATES.includes(kept) ? kept : 1;
  } catch {
    return 1;
  }
}

function StreamPlayer({
  session,
  onProgress,
  arrivals,
  onAnswer,
  chrome = "room",
  player,
  viewerEmail = "",
  onConvert,
  onOfferVideo,
  reactions,
  captions,
  autoPlay = false,
  onPlayState,
}: PlayerProps): ReactNode {
  const bare = chrome !== "room";
  const sheet = chrome === "sheet";
  const url = session.playback_url as string;
  // V8: gated R2/HLS carries a signed session token. We append it to the MASTER
  // request only (`?t=`) to bootstrap the `fw_media` cookie; hls.js resolves the
  // variant + segment URLs relatively (query dropped), so they stay token-free and
  // edge-cacheable, and authorize via the cookie (withCredentials). A Stream / mock
  // URL has no token → nothing changes on the live path.
  const token = session.media_token;
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const seekedToSaved = useRef(false);
  const lastPosted = useRef(0);
  /** Where the last `timeupdate` left the playhead: one half of a crossing. */
  const lastTick = useRef(session.watched_sec);
  /** And when that was, by the wall clock, which is what a seek is judged against. */
  const lastTickAt = useRef(Date.now());
  /** Did WE pause the talk for a moment? Then we are the ones to start it again. */
  const heldForMoment = useRef(false);
  const [cur, setCur] = useState(session.watched_sec);
  const [dur, setDur] = useState(session.duration_sec || 0);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    onPlayState?.(playing);
  }, [playing, onPlayState]);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [idle, setIdle] = useState(false);
  /** The talk has run to its end and the picture has given way to the end card. */
  const [ended, setEnded] = useState(false);
  /**
   * The offer in the picture: "card" once the talk crosses its minute, "tab"
   * after the viewer closes it (or when the talk was already past it when the
   * player opened, so nothing pops at them), and "off" before it lands.
   */
  const [offerShow, setOfferShow] = useState<"off" | "card" | "tab">(() =>
    session.offer && offerArrived(session.offer, session.watched_sec) ? "tab" : "off",
  );
  // How fast the talk plays, remembered in this browser across talks.
  const [rate, setRate] = useState<number>(() => savedRate());

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
    try {
      localStorage.setItem(RATE_KEY, String(rate));
    } catch {
      // A browser that keeps nothing still plays at the chosen speed.
    }
  }, [rate]);

  // Attach the HLS source (native where supported, else hls.js). Depends only on
  // the URL, so it never re-runs while the watch position changes.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hlsInstance: { destroy: () => void } | null = null;
    let cancelled = false;

    const src = token ? `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}` : url;

    // hls.js first: a browser can claim native HLS and then fail to play it
    // (hls.js documents Chrome doing exactly that), so the element's own player
    // is only the fallback for Safari-family browsers without MSE.
    const native = () => {
      // Native HLS (Safari): send the media cookie on the cross-origin segment
      // fetches it makes off the master response.
      if (token) video.crossOrigin = "use-credentials";
      video.src = src;
    };
    {
      void import("hls.js")
        .then(({ default: Hls }) => {
          if (cancelled) return;
          if (Hls.isSupported()) {
            // withCredentials → the `fw_media` cookie rides every request (the token
            // is NOT in the URL, so segment cache keys stay viewer-independent).
            const hls = new Hls(
              token
                ? { enableWorker: true, xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; } }
                : { enableWorker: true },
            );
            // One recovery each before giving up: a network error reloads the
            // source (which re-installs the media cookie from `?t=`), a media error
            // asks hls.js to recover. A second fatal of either kind is the end.
            let retriedNetwork = false;
            let retriedMedia = false;
            hls.on(Hls.Events.ERROR, (_e, data) => {
              if (!data.fatal) return;
              if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !retriedNetwork) {
                retriedNetwork = true;
                const at = video.currentTime;
                hls.loadSource(src);
                hls.startLoad(at);
                return;
              }
              if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !retriedMedia) {
                retriedMedia = true;
                hls.recoverMediaError();
                return;
              }
              setFailed(true);
            });
            hls.loadSource(src);
            hls.attachMedia(video);
            hlsInstance = hls;
          } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            native();
          } else {
            setFailed(true);
          }
        })
        .catch(() => {
          if (video.canPlayType("application/vnd.apple.mpegurl")) native();
          else setFailed(true);
        });
    }

    return () => {
      cancelled = true;
      hlsInstance?.destroy();
    };
  }, [url, token]);

  const flush = useCallback(
    (sec: number) => {
      const total = dur || session.duration_sec || 1;
      onProgress(sec, (sec / total) * 100);
      lastPosted.current = sec;
    },
    [dur, session.duration_sec, onProgress],
  );

  const onLoadedMeta = () => {
    const video = videoRef.current;
    if (!video) return;
    if (Number.isFinite(video.duration) && video.duration > 0) setDur(video.duration);
    // Resume once at the saved place.
    if (!seekedToSaved.current && session.watched_sec > 0) {
      seekedToSaved.current = true;
      try {
        video.currentTime = session.watched_sec;
      } catch {
        /* seeking before ready is a no-op */
      }
    }
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const now = video.currentTime;
    const from = lastTick.current;
    const at = Date.now();
    const wall = (at - lastTickAt.current) / 1000;
    lastTick.current = now;
    lastTickAt.current = at;
    setCur(now);
    // A moment fires only when the talk PLAYS across its minute. Mid-seek the
    // element reports positions it jumped to, and those cross nothing. A slow
    // frame is not a seek: the wall clock says how far the talk could have got.
    if (!video.seeking) {
      arrivals.onTime({ from, to: now, playing: !video.paused, wall, rate: video.playbackRate });
    }
    if (Math.abs(now - lastPosted.current) >= PROGRESS_POST_EVERY_SEC) flush(now);
  };

  /**
   * The talk waits behind a moment that asks it to, and starts again after.
   *
   * `heldForMoment` is who paused it. Set only when WE stopped a talk that was
   * running, or when the viewer asked for it back while the moment held it. A
   * viewer who had already paused the talk themselves gets it left alone.
   */
  const held = arrivals.paused;
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (held) {
      if (!video.paused) {
        heldForMoment.current = true;
        video.pause();
      }
      return;
    }
    if (!heldForMoment.current) return;
    heldForMoment.current = false;
    void video.play().catch(() => {
      /* the browser would not start it again; the controls still work */
    });
  }, [held]);

  // The one place every way of starting the talk meets: the play button, the
  // mini play, a click on the picture, the keyboard, or the browser's own
  // controls. While a moment holds the talk, it stays held.
  const onPlay = () => {
    const video = videoRef.current;
    if (held && video) {
      heldForMoment.current = true;
      video.pause();
      return;
    }
    setPlaying(true);
  };

  // D3: one thing over the picture at a time. A moment arriving puts the
  // chapter list away rather than landing on top of it.
  useEffect(() => {
    if (!arrivals.up) return;
    setChaptersOpen(false);
    setOfferShow((show) => (show === "card" ? "tab" : show));
  }, [arrivals.up]);

  // The offer lands in the picture the moment the talk plays across its minute.
  const offerAt = session.offer?.appears_at;
  useEffect(() => {
    if (!session.offer || offerShow !== "off") return;
    if (offerArrived(session.offer, cur)) setOfferShow("card");
  }, [session.offer, offerAt, cur, offerShow]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
  };

  // The Watch page's bar and pill get out of the way while the talk plays, and
  // come back on the first sign of the viewer.
  const idleTimer = useRef(0);
  const stillOn = useRef({ playing: false, chapters: false });
  stillOn.current = { playing, chapters: chaptersOpen };
  const wake = useCallback(() => {
    setIdle((was) => (was ? false : was));
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      if (stillOn.current.playing && !stillOn.current.chapters) setIdle(true);
    }, IDLE_MS);
  }, []);
  useEffect(() => {
    if (playing) return;
    window.clearTimeout(idleTimer.current);
    setIdle(false);
  }, [playing]);
  useEffect(() => () => window.clearTimeout(idleTimer.current), []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const toggleFull = () => {
    const box = boxRef.current;
    if (!box) return;
    const leaving = typeof document !== "undefined" && document.fullscreenElement !== null;
    if (!leaving && typeof box.requestFullscreen !== "function") {
      // An iPhone has no full screen for a box, only for the video itself.
      const native = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
      native?.webkitEnterFullscreen?.();
      return;
    }
    void (leaving ? document.exitFullscreen?.() : box.requestFullscreen?.())?.catch(() => {
      /* a browser that will not go full screen leaves the player where it is */
    });
  };

  /** Jump the talk. A jump crosses nothing, so `lastTick` moves with it. */
  const jumpTo = useCallback(
    (sec: number) => {
      const video = videoRef.current;
      if (video) video.currentTime = sec;
      lastTick.current = sec;
      lastTickAt.current = Date.now();
      setCur(sec);
      setEnded(false);
      flush(sec);
    },
    [flush],
  );

  useEffect(() => {
    if (!player) return;
    player.current = { jumpTo, pause: () => videoRef.current?.pause() };
    return () => {
      player.current = null;
    };
  }, [player, jumpTo]);

  const total = dur || session.duration_sec || 1;
  const pct = Math.max(0, Math.min(100, (cur / total) * 100));
  const scrub = useScrubDrag((frac) => jumpTo(frac * total));
  const shown = scrub.drag === null ? pct : scrub.drag * 100;
  const rx = useReplayReactions(reactions, {
    cur,
    total,
    playing,
    playFrom: (sec) => {
      jumpTo(sec);
      void videoRef.current?.play().catch(() => {});
    },
  });
  const chapterAt = currentChapter(session.chapters, cur);
  // Before the first mark the talk is still inside the opening chapter.
  const chapterNow = session.chapters[chapterAt >= 0 ? chapterAt : 0]?.title ?? "";
  // The sheet: the picture is the control, as the prototype draws it.
  if (sheet) {
    const said = playing && captions ? captionAt(captions, cur) : "";
    return (
      <>
        <div
          ref={boxRef}
          className={`player${playing ? " playing" : ""}`}
          data-dur={Math.round(total)}
          role="button"
          tabIndex={0}
          aria-label={playing ? "Pause the talk" : "Play the talk"}
          onClick={togglePlay}
          onKeyDown={(e) => {
            const video = videoRef.current;
            if (e.target !== e.currentTarget || !video) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              togglePlay();
            }
            if (e.key === "ArrowRight") {
              jumpTo(Math.min(total, video.currentTime + 5));
              e.preventDefault();
            }
            if (e.key === "ArrowLeft") {
              jumpTo(Math.max(0, video.currentTime - 5));
              e.preventDefault();
            }
          }}
        >
          <video
            ref={videoRef}
            playsInline
            autoPlay={autoPlay}
            poster={session.poster_url ?? undefined}
            onLoadedMetadata={onLoadedMeta}
            onDurationChange={onLoadedMeta}
            onTimeUpdate={onTimeUpdate}
            onPlay={onPlay}
            onPause={() => {
              setPlaying(false);
              const video = videoRef.current;
              if (video) flush(video.currentTime);
            }}
            onEnded={() => {
              setPlaying(false);
              onProgress(Math.round(total), 100);
            }}
          >
            <track kind="captions" />
          </video>
          <div className="shade" />
          <div className="pl-cap" hidden={!said}>
            <span>{said}</span>
          </div>
          <span className="vplay">{playing ? <IcPause s={26} /> : <IcPlay s={26} />}</span>
          <div className="pl-bot">
            <div className="pl-row">
              <span>{mmss(cur)}</span>
              <span>{mmss(total)}</span>
            </div>
            <div
              className="pl-bar"
              role="slider"
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(pct)}
              {...scrub.handlers}
              onClick={(e) => e.stopPropagation()}
            >
              <i style={{ width: `${shown}%` }} />
              {session.chapters
                .filter((ch) => ch.t > 0)
                .map((ch) => (
                  <u key={`${ch.t}-${ch.title}`} style={{ left: `${(ch.t / total) * 100}%` }} />
                ))}
            </div>
          </div>
          {failed ? <p className="pl-fail">The recording stopped playing. Refresh the page to continue.</p> : null}
        </div>
        {arrivals.up ? (
          <div className="w-momentsheet">
            <MomentArrival interaction={arrivals.up} waiting={arrivals.waiting} onAnswer={onAnswer} onDismiss={arrivals.dismiss} />
          </div>
        ) : null}
      </>
    );
  }

  // The row: play, the scrub with the chapter gaps, the time, the speed, the
  // sound and full screen, on the picture as `.w-controls`.
  const controls = (

    <div className={`w-controls${rx ? " w-rx-on" : ""}`}>
      <button
        className="w-mini-play"
        aria-label={bare ? (playing ? "Pause" : "Play") : "Play/pause"}
        type="button"
        onClick={togglePlay}
      >
        {bare && playing ? <IcPauseF /> : <IcPlayF />}
      </button>
      <div
        className={scrub.drag === null ? "w-scrub" : "w-scrub w-dragging"}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        tabIndex={0}
        {...scrub.handlers}
        onKeyDown={(e) => {
          const video = videoRef.current;
          if (!video) return;
          if (e.key === "ArrowRight") { video.currentTime = Math.min(total, video.currentTime + 5); e.preventDefault(); }
          if (e.key === "ArrowLeft") { video.currentTime = Math.max(0, video.currentTime - 5); e.preventDefault(); }
        }}
      >
        {rx?.strip}
        <div className="w-fill" style={{ width: `${shown}%` }} />
        {bare
          ? session.chapters
              .filter((ch) => ch.t > 0)
              .map((ch) => (
                <div
                  key={`${ch.t}-${ch.title}`}
                  className="w-gap"
                  style={{ left: `${(ch.t / total) * 100}%` }}
                />
              ))
          : null}
        {bare
          ? session.interactions.map((it) => (
              <button
                key={it.id}
                type="button"
                className={it.appears_at < cur ? "w-mdot w-past" : "w-mdot"}
                style={{ left: `${(it.appears_at / total) * 100}%` }}
                aria-label={`${momentTitle(it)}, at ${mmss(it.appears_at)}`}
                data-tip={`${mmss(it.appears_at)}  ${momentTitle(it)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  jumpTo(it.appears_at);
                }}
              />
            ))
          : session.interactions.map((it) => (
              <div
                key={it.id}
                className="w-mark"
                style={{ left: `${(it.appears_at / total) * 100}%` }}
                title={`${it.type} · ${mmss(it.appears_at)}`}
              />
            ))}
        <div className="w-knob" style={{ left: `${shown}%` }} />
      </div>
      <span className="w-tc"><span className="w-num">{mmss(Math.round(cur))}</span> / <span className="w-num" style={{ opacity: 0.7 }}>{mmss(Math.round(total))}</span></span>
      {!bare && session.chapters.length > 0 ? (
        <button
          type="button"
          className="w-chapbtn"
          aria-expanded={chaptersOpen}
          onClick={() => setChaptersOpen((open) => !open)}
        >
          Chapters
        </button>
      ) : null}
      {rx?.buttons}
      {bare ? (
        <>
          <button
            type="button"
            className="w-speed"
            aria-label={`Playback speed, ${rateLabel(rate)}`}
            title="Playback speed"
            onClick={() => setRate(nextRate(rate))}
          >
            {rateLabel(rate)}
          </button>
          <button
            type="button"
            className="w-ctrlbtn"
            aria-label={muted ? "Sound on" : "Sound off"}
            onClick={toggleMute}
          >
            {muted ? <IcMute /> : <IcVol />}
          </button>
          <button type="button" className="w-ctrlbtn" aria-label="Full screen" onClick={toggleFull}>
            <IcExpand />
          </button>
          {rx?.card}
        </>
      ) : (
        <>
          <IcVol className="w-ctrl-ic" />
          <IcExpand className="w-ctrl-ic" />
        </>
      )}
    </div>
  );

  return (
    <div className="w-playerwrap">
      <div
        ref={boxRef}
        className={`w-player${arrivals.up ? " w-hasmoment" : ""}${bare ? " w-bare" : ""}${bare && idle && !ended ? " w-idle" : ""}${ended ? " w-ended" : ""}`}
        data-dur={Math.round(total)}
        style={{ aspectRatio: "16 / 9" }}
        onMouseMove={bare ? wake : undefined}
        onMouseLeave={bare ? () => setIdle(playing) : undefined}
      >
        <video
          ref={videoRef}
          className="w-video"
          playsInline
          poster={session.poster_url ?? undefined}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            // Contain, never cover: the box is 16:9, but a full screen is not,
            // and a talk shot 4:3 is not either. Cover zoomed and cropped both.
            objectFit: "contain",
            // The box behind it is the page's surface in light mode and near
            // black in dark, so a sliver at the edge never reads as a border.
            background: "transparent",
          }}
          onLoadedMetadata={onLoadedMeta}
          onDurationChange={onLoadedMeta}
          onTimeUpdate={onTimeUpdate}
          onPlay={onPlay}
          onPause={() => {
            setPlaying(false);
            const video = videoRef.current;
            if (video) flush(video.currentTime);
          }}
          onEnded={() => {
            setPlaying(false);
            onProgress(Math.round(total), 100);
            if (bare) setEnded(true);
          }}
          onClick={togglePlay}
        >
          <track kind="captions" />
        </video>

        {bare ? null : (
          <>
            <div className="w-chip-live"><span className="w-rdot" /> Replay · {mmss(Math.round(total))}</div>
            {session.watched_sec > 0 ? (
              <div className="w-chip-dur"><span className="w-num">Watched to {mmss(session.watched_sec)}</span></div>
            ) : null}
          </>
        )}

        {!playing && !ended ? (
          <button className="w-playbtn" aria-label="Play the replay" type="button" onClick={togglePlay}>
            <IcPlayF />
          </button>
        ) : null}

        {failed ? (
          <div className="w-chip-dur" style={{ top: "auto", bottom: 64 }}>
            The recording stopped playing. Refresh the page to continue.
          </div>
        ) : null}

        {bare ? null : (
          <div className="w-lower-third">
            <div className="w-lt-mark">{initials(session.host_name)}</div>
            <div>
              <div className="w-lt-name">{session.host_name}</div>
              <div className="w-lt-role">Replay · your saved place</div>
            </div>
          </div>
        )}

        {rx?.lane}
        {rx?.note}

        {bare && session.chapters.length > 0 && !ended ? (
          <button
            type="button"
            className="w-chappill w-chapico"
            aria-expanded={chaptersOpen}
            aria-label={`Chapters, now ${chapterNow}`}
            title={chapterNow}
            onClick={() => setChaptersOpen((open) => !open)}
          >
            <IcList />
          </button>
        ) : null}

        {controls}

        {chaptersOpen ? (
          <ChapterList
            chapters={session.chapters}
            currentIndex={chapterAt}
            onJump={(sec) => {
              jumpTo(sec);
              setChaptersOpen(false);
            }}
          />
        ) : null}

        <MomentArrival
          interaction={arrivals.up}
          waiting={arrivals.waiting}
          onAnswer={onAnswer}
          onDismiss={arrivals.dismiss}
        />

        {/* The offer lives in the picture. A moment on screen takes the corner,
            so the card waits as a tab until it has gone. */}
        {bare && !ended && session.offer && !offerClosed(session.offer, cur) && offerShow === "card" && !arrivals.up ? (
          <OfferCard
            offer={session.offer}
            hostName={session.host_name}
            viewerEmail={viewerEmail}
            onConvert={onConvert}
            onClose={() => setOfferShow("tab")}
            onPlayNote={() => videoRef.current?.pause()}
            onNote={onOfferVideo}
          />
        ) : null}
        {bare && !ended && session.offer && !offerClosed(session.offer, cur) && (offerShow === "tab" || (offerShow === "card" && arrivals.up)) ? (
          <OfferTab offer={session.offer} onOpen={() => setOfferShow("card")} />
        ) : null}

        {ended ? (
          <EndCard
            offer={session.offer}
            hostName={session.host_name}
            viewerEmail={viewerEmail}
            onConvert={onConvert}
            onAgain={() => {
              jumpTo(0);
              void videoRef.current?.play().catch(() => {});
            }}
          />
        ) : null}
      </div>
      {bare ? null : (
        <div className="w-player-cap">
          <IcShield />
          Pick up right where you left off. Your place is saved.
        </div>
      )}
    </div>
  );
}

/**
 * The branded placeholder shown until a real recording is ready to play. There
 * is nothing playing here, so no moment ever arrives on it; the chapter list is
 * still worth showing, because it is a map of the talk they are waiting for.
 */
function PlaceholderPlayer({ session, onProgress, chrome = "room", player }: PlayerProps): ReactNode {
  const bare = chrome !== "room";
  const dur = session.duration_sec || 1;
  const pct = Math.max(0, Math.min(100, session.watched_pct));
  const [chaptersOpen, setChaptersOpen] = useState(false);
  // Nothing is playing here, so a jump is only a new saved place.
  useEffect(() => {
    if (!player) return;
    player.current = {
      jumpTo: (sec) => onProgress(sec, (sec / dur) * 100),
      pause: () => {},
    };
    return () => {
      player.current = null;
    };
  }, [player, onProgress, dur]);
  const scrub = useScrubDrag((frac) => onProgress(frac * dur, frac * 100));
  const shown = scrub.drag === null ? pct : scrub.drag * 100;
  const seekByPct = (nextPct: number) => {
    const clamped = Math.max(0, Math.min(100, nextPct));
    onProgress((clamped / 100) * dur, clamped);
  };
  // The sheet draws its own look with nothing to play: the picture, the saved place and the chapters' ticks.
  if (chrome === "sheet")
    return (
      <div className="player" data-dur={session.duration_sec}>
        {session.poster_url ? <img src={session.poster_url} alt="" /> : null}
        <div className="shade" />
        <span className="vplay">
          <IcPlay s={26} />
        </span>
        <div className="pl-bot">
          <div className="pl-row">
            <span>{mmss(session.watched_sec)}</span>
            <span>{mmss(session.duration_sec)}</span>
          </div>
          <div className="pl-bar">
            <i style={{ width: `${pct}%` }} />
            {session.chapters
              .filter((ch) => ch.t > 0)
              .map((ch) => (
                <u key={`${ch.t}-${ch.title}`} style={{ left: `${(ch.t / dur) * 100}%` }} />
              ))}
          </div>
        </div>
      </div>
    );
  return (
    <div className="w-playerwrap">
      <div className={`w-player${bare ? " w-bare" : ""}`} data-dur={session.duration_sec}>
        <svg className="w-stage" viewBox="0 0 960 540" role="img" aria-label={`${session.host_name} presenting ${session.title}`}>
          <defs>
            <linearGradient id="w-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2a1a20" /><stop offset="1" stopColor="#150e11" /></linearGradient>
            <radialGradient id="w-spot" cx="50%" cy="24%" r="60%"><stop offset="0" stopColor="rgba(176,135,58,.34)" /><stop offset="1" stopColor="rgba(176,135,58,0)" /></radialGradient>
            <linearGradient id="w-floor" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1d1317" /><stop offset="1" stopColor="#100a0d" /></linearGradient>
            <linearGradient id="w-screen" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3a232c" /><stop offset="1" stopColor="#221419" /></linearGradient>
            <linearGradient id="w-skin" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f3dcc8" /><stop offset="1" stopColor="#e6c3aa" /></linearGradient>
          </defs>
          <rect width="960" height="540" fill="url(#w-bg)" />
          <rect width="960" height="540" fill="url(#w-spot)" />
          <rect x="118" y="70" width="724" height="300" rx="12" fill="url(#w-screen)" opacity=".9" />
          <rect x="118" y="70" width="724" height="300" rx="12" fill="none" stroke="rgba(176,135,58,.28)" />
          <text x="160" y="164" fill="rgba(240,233,236,.92)" fontFamily="var(--serif)" fontSize="34" fontWeight="700">{session.title}</text>
          <rect x="160" y="188" width="150" height="4" rx="2" fill="rgba(176,135,58,.8)" />
          <g fill="rgba(240,233,236,.42)">
            <rect x="160" y="220" width="300" height="9" rx="4" />
            <rect x="160" y="242" width="240" height="9" rx="4" />
            <rect x="160" y="264" width="270" height="9" rx="4" />
          </g>
          <g transform="translate(560,244)">
            <rect x="0" y="40" width="26" height="60" rx="3" fill="rgba(122,35,56,.75)" />
            <rect x="40" y="14" width="26" height="86" rx="3" fill="rgba(176,135,58,.85)" />
            <rect x="80" y="60" width="26" height="40" rx="3" fill="rgba(122,35,56,.55)" />
            <rect x="120" y="0" width="26" height="100" rx="3" fill="rgba(207,125,146,.85)" />
          </g>
          <rect x="0" y="392" width="960" height="148" fill="url(#w-floor)" />
          <ellipse cx="300" cy="470" rx="180" ry="30" fill="rgba(0,0,0,.4)" />
          <g transform="translate(230,250)">
            <path d="M-56 240 C-56 150 -34 118 0 118 C34 118 56 150 56 240 Z" fill="#5f1a2b" />
            <path d="M-30 130 L0 240 L30 130 Z" fill="#7a2338" />
            <rect x="-12" y="96" width="24" height="34" rx="10" fill="url(#w-skin)" />
            <circle cx="0" cy="70" r="34" fill="url(#w-skin)" />
            <path d="M-36 66 C-40 24 -22 6 0 6 C22 6 40 24 36 66 C30 44 22 36 22 36 C14 30 -14 30 -22 36 C-22 36 -30 44 -36 66 Z" fill="#2f2126" />
            <circle cx="-11" cy="70" r="2.4" fill="#3a2a2f" />
            <circle cx="11" cy="70" r="2.4" fill="#3a2a2f" />
            <path d="M-9 84 Q0 90 9 84" fill="none" stroke="#b06a5e" strokeWidth="2.4" strokeLinecap="round" />
          </g>
          <g transform="translate(470,300)">
            <path d="M0 0 L120 0 L150 190 L-30 190 Z" fill="#3a2830" />
            <rect x="-6" y="-8" width="132" height="20" rx="5" fill="#4a333c" />
            <rect x="20" y="30" width="80" height="6" rx="3" fill="rgba(176,135,58,.5)" />
          </g>
        </svg>

        {bare ? null : (
          <>
            <div className="w-chip-live"><span className="w-rdot" /> Replay · {mmss(session.duration_sec)}</div>
            <div className="w-chip-dur"><span className="w-num">Watched to {mmss(session.watched_sec)}</span></div>
          </>
        )}

        <button className="w-playbtn" aria-label="Play the replay" type="button">
          <IcPlayF />
        </button>

        {bare ? null : (
          <div className="w-lower-third">
            <div className="w-lt-mark">{initials(session.host_name)}</div>
            <div>
              <div className="w-lt-name">{session.host_name}</div>
              <div className="w-lt-role">Replay · your saved place</div>
            </div>
          </div>
        )}

        {bare && session.chapters.length > 0 ? (
          <button
            type="button"
            className="w-chappill w-chapico"
            aria-expanded={chaptersOpen}
            aria-label={`Chapters, now ${session.chapters[Math.max(0, currentChapter(session.chapters, session.watched_sec))].title}`}
            title={session.chapters[Math.max(0, currentChapter(session.chapters, session.watched_sec))].title}
            onClick={() => setChaptersOpen((open) => !open)}
          >
            <IcList />
          </button>
        ) : null}

        <div className="w-controls">
          <button className="w-mini-play" aria-label="Play/pause" type="button"><IcPlayF /></button>
          <div
            className={scrub.drag === null ? "w-scrub" : "w-scrub w-dragging"}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            tabIndex={0}
            {...scrub.handlers}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") { seekByPct(pct + 2); e.preventDefault(); }
              if (e.key === "ArrowLeft") { seekByPct(pct - 2); e.preventDefault(); }
            }}
          >
            <div className="w-fill" style={{ width: `${shown}%` }} />
            {bare
              ? session.chapters
                  .filter((ch) => ch.t > 0)
                  .map((ch) => (
                    <div
                      key={`${ch.t}-${ch.title}`}
                      className="w-gap"
                      style={{ left: `${(ch.t / dur) * 100}%` }}
                    />
                  ))
              : session.interactions.map((it) => (
                  <div
                    key={it.id}
                    className="w-mark"
                    style={{ left: `${(it.appears_at / dur) * 100}%` }}
                    title={`${it.type} · ${mmss(it.appears_at)}`}
                  />
                ))}
            <div className="w-knob" style={{ left: `${shown}%` }} />
          </div>
          <span className="w-tc"><span className="w-num">{mmss(session.watched_sec)}</span> / <span className="w-num" style={{ opacity: 0.7 }}>{mmss(session.duration_sec)}</span></span>
          {!bare && session.chapters.length > 0 ? (
            <button
              type="button"
              className="w-chapbtn"
              aria-expanded={chaptersOpen}
              onClick={() => setChaptersOpen((open) => !open)}
            >
              Chapters
            </button>
          ) : null}
          <IcVol className="w-ctrl-ic" />
          <IcExpand className="w-ctrl-ic" />
        </div>

        {chaptersOpen ? (
          <ChapterList
            chapters={session.chapters}
            currentIndex={currentChapter(session.chapters, session.watched_sec)}
            onJump={(sec) => {
              onProgress(sec, (sec / dur) * 100);
              setChaptersOpen(false);
            }}
          />
        ) : null}
      </div>
      {bare ? null : (
        <div className="w-player-cap">
          <IcShield />
          Pick up right where you left off. Your place is saved.
        </div>
      )}
    </div>
  );
}

// ── the chapter list, in the player ─────────────────────────────────────
/**
 * The chapters the host switched on, in the player. Clicking one jumps the talk
 * there; the one the playhead is inside is marked. Nothing at all when the talk
 * has no chapters.
 */
export function ChapterList({
  chapters,
  currentIndex,
  onJump,
}: {
  chapters: readonly WatchChapter[];
  currentIndex: number;
  onJump: (seconds: number) => void;
}): ReactNode {
  if (chapters.length === 0) return null;
  return (
    <div className="w-chaplist">
      <h4>Chapters</h4>
      <ol>
        {chapters.map((chapter, i) => (
          <li key={`${chapter.t}-${chapter.title}`}>
            <button
              type="button"
              className={`w-chapjump${i === currentIndex ? " w-sel" : ""}`}
              aria-current={i === currentIndex ? "true" : undefined}
              onClick={() => onJump(chapter.t)}
            >
              <span className="w-chapt w-num">{mmss(chapter.t)}</span>
              <span>
                <span className="w-chaptitle" dir="auto">{chapter.title}</span>
                {chapter.sub ? <span className="w-chapsub" dir="auto">{chapter.sub}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Everything inside a modal moment that a Tab can land on. */
const FOCUS_STOPS =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// ── one timed Moment ────────────────────────────────────────────────────
export type AnswerFn = (interactionId: string, value: WatchSession["interactions"][number]["my_response"]) => void;

/**
 * A moment, as a card. One skeleton for all four kinds: the kind in its own
 * colour, the minute, what was asked, the one thing to do, and the way out.
 *
 * It only ever COLLECTS. Nothing about the room is ever drawn here: no bars, no
 * percentages, no counts, nothing about who else answered. The first viewer of
 * a replay would otherwise be told that one person answered, which says nothing
 * about anything. The host has all of it in their own views.
 */
function MomentCard({
  interaction,
  onAnswer,
  now = false,
  later = null,
}: {
  interaction: WatchSession["interactions"][number];
  onAnswer: AnswerFn;
  /** On the player, arriving: it is happening now, not at a minute in a list. */
  now?: boolean;
  /** The way out, when there is one: it sits in the card's own footer row. */
  later?: ReactNode;
}): ReactNode {
  const kind = interaction.type;
  const kindLabel = kind === "poll" ? "Poll" : kind === "question" ? "Quick question" : kind === "reaction" ? "Reactions" : "Handout";
  return (
    <div className="w-mcard" data-k={kind}>
      <div className="w-mhead">
        <span className="w-mkind"><i className="w-kdot" />{kindLabel}</span>
        <span className="w-mtime">
          {now ? "now" : <span className="w-num">{mmss(interaction.appears_at)}</span>}
        </span>
      </div>
      {kind === "poll" ? <Poll interaction={interaction} onAnswer={onAnswer} later={later} /> : null}
      {kind === "question" ? <QuestionCard interaction={interaction} onAnswer={onAnswer} later={later} arriving={now} /> : null}
      {kind === "reaction" ? <ReactionCard interaction={interaction} onAnswer={onAnswer} later={later} /> : null}
      {kind === "handout" ? <Handout interaction={interaction} onAnswer={onAnswer} later={later} /> : null}
    </div>
  );
}

// ── one Moment, arriving on the player ──────────────────────────────────
/**
 * A moment as the host previewed it: where they put it, the "Paused" pill when
 * the talk waits behind it, the "1 waiting" pill when another one is queued, and
 * Later to send it to the Moments tab. Answering inside it posts through the same
 * respond call the Moments tab uses, and shows the same result.
 *
 * The card takes focus when it arrives, so Escape dismisses it straight away; the
 * document listener keeps Escape working after the viewer clicks elsewhere.
 * Focus moves ONLY when a moment arrives (its id changes), never on a re-render
 * of the same moment: the board previews one live while the host types into
 * it, and a preview that took the caret on every keystroke was a bug. The
 * board's previews pass `takeFocus={false}` and never take it at all.
 */
export function MomentArrival({
  interaction,
  waiting,
  onAnswer,
  onDismiss,
  takeFocus = true,
}: {
  interaction: WatchInteraction | null;
  waiting: number;
  onAnswer: AnswerFn;
  onDismiss: (interactionId: string) => void;
  /** Whether an arriving card takes the keyboard (a viewer's does; a preview's must not). */
  takeFocus?: boolean;
}): ReactNode {
  const card = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const id = interaction?.id ?? null;
  // Full screen covers the talk, so it is a real modal: nothing behind it is
  // reachable while it is up, and focus goes back where it came from after.
  const modal = interaction?.show.where === "full";
  // The latest dismiss, read at key time, so a caller passing a fresh function
  // on every render never re-runs the arrival (and its focus move) below.
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (!id) return;
    if (takeFocus) {
      returnTo.current = document.activeElement as HTMLElement | null;
      card.current?.focus({ preventScroll: true });
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss.current(id);
        return;
      }
      if (e.key !== "Tab" || !modal) return;
      const box = card.current;
      if (!box) return;
      const stops = [box, ...Array.from(box.querySelectorAll<HTMLElement>(FOCUS_STOPS))].filter(
        (el) => !(el as HTMLButtonElement).disabled,
      );
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      const outside = !active || !box.contains(active);
      if (e.shiftKey ? outside || active === first : outside || active === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus({ preventScroll: true });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (takeFocus) returnTo.current?.focus?.({ preventScroll: true });
    };
  }, [id, modal, takeFocus]);

  if (!interaction || !id) return null;
  const show = interaction.show;
  return (
    <>
      {show.pause ? <div className="w-pausepill">Paused</div> : null}
      {waiting > 0 ? (
        <div className="w-waitpill">{waiting} waiting</div>
      ) : null}
      <div className={`w-arrive w-at-${show.where}`}>
        <div
          className="w-arrivecard"
          ref={card}
          role="dialog"
          aria-modal={modal || undefined}
          aria-label="A moment from the talk"
          tabIndex={0}
        >
          <MomentCard
            interaction={interaction}
            onAnswer={onAnswer}
            now
            later={
              <button type="button" className="w-arrivelater" onClick={() => onDismiss(id)}>
                {interaction.my_response ? "Close" : "Later"}
              </button>
            }
          />
        </div>
      </div>
    </>
  );
}

/** The green line every kind ends on once the viewer has answered. */
function Thanks({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="w-thanks">
      <IcCheck />
      {children}
    </div>
  );
}

function Poll({ interaction, onAnswer, later }: { interaction: WatchSession["interactions"][number]; onAnswer: AnswerFn; later?: ReactNode }): ReactNode {
  const cfg = interaction.config as PollConfig;
  const mine = interaction.my_response;
  const picked = mine && mine.kind === "poll" ? mine.option : null;
  const answered = picked !== null;
  // Answered, the options they did not pick have nothing left to say.
  const options = (cfg.options ?? []).filter((o) => !answered || o.label === picked);
  return (
    <>
      <div className="w-q">{cfg.question}</div>
      <div className={`w-poll${answered ? " w-done" : ""}`}>
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            className={`w-opt${picked === o.label ? " w-picked" : ""}`}
            onClick={() => !answered && onAnswer(interaction.id, { kind: "poll", option: o.label })}
          >
            <span className="w-olabel">{o.label}</span>
          </button>
        ))}
      </div>
      {answered ? (
        <Thanks>Thanks, got it.</Thanks>
      ) : (
        <div className="w-mrow">
          <span>Tap your answer.</span>
          {later}
        </div>
      )}
    </>
  );
}

function QuestionCard({ interaction, onAnswer, later, arriving = false }: { interaction: WatchSession["interactions"][number]; onAnswer: AnswerFn; later?: ReactNode; arriving?: boolean }): ReactNode {
  const cfg = interaction.config as QuestionConfig;
  const mine = interaction.my_response;
  const sent = !!(mine && mine.kind === "question");
  const [text, setText] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  // The card lands and the answer can be typed straight away. Only the one
  // ARRIVING takes the caret: a list of them must not fight over it.
  useEffect(() => {
    if (arriving && !sent) field.current?.focus({ preventScroll: true });
  }, [arriving, sent]);
  const send = () => {
    const v = text.trim();
    if (!v) return;
    onAnswer(interaction.id, { kind: "question", text: v });
    setText("");
  };
  return (
    <>
      <div className="w-q">{cfg.question}</div>
      {cfg.sub ? <div className="w-qsub" dir="auto">{cfg.sub}</div> : null}
      {sent ? (
        <Thanks>Sent.</Thanks>
      ) : (
        <>
          <textarea dir="auto"
            ref={field}
            className="w-qfield"
            placeholder="Type your answer"
            aria-label="Your answer"
            maxLength={ANSWER_MAX}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, ANSWER_MAX))}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              e.preventDefault();
              send();
            }}
          />
          <div className="w-mrow">
            <span className="w-count w-num">{`${text.length} / ${ANSWER_MAX}`}</span>
            <span className="w-acts">
              {later}
              <button type="button" className="w-send" disabled={!text.trim()} onClick={send}>
                Send
              </button>
            </span>
          </div>
        </>
      )}
    </>
  );
}

function ReactionCard({ interaction, onAnswer, later }: { interaction: WatchSession["interactions"][number]; onAnswer: AnswerFn; later?: ReactNode }): ReactNode {
  const cfg = interaction.config as ReactionConfig;
  const mine = interaction.my_response;
  const reacted = mine && mine.kind === "reaction" ? mine.emoji : null;
  // Once they have tapped one, theirs is the only one left on the card.
  const reactions = (cfg.reactions ?? []).filter((r) => !reacted || r.emoji === reacted);
  return (
    <>
      <div className="w-q">{cfg.prompt}</div>
      <div className="w-emo">
        {reactions.map((r) => (
          <button
            key={r.emoji}
            type="button"
            className={`w-react${reacted === r.emoji ? " w-on" : ""}`}
            aria-label={`React ${r.emoji}`}
            onClick={() => !reacted && onAnswer(interaction.id, { kind: "reaction", emoji: r.emoji })}
          >
            <b>{r.emoji}</b>
          </button>
        ))}
      </div>
      {reacted ? (
        <Thanks>Thanks.</Thanks>
      ) : (
        <div className="w-mrow">
          <span>Tap one.</span>
          {later}
        </div>
      )}
    </>
  );
}

function Handout({ interaction, onAnswer, later }: { interaction: WatchSession["interactions"][number]; onAnswer: AnswerFn; later?: ReactNode }): ReactNode {
  const cfg = interaction.config as HandoutConfig;
  const mine = interaction.my_response;
  const saved = !!(mine && mine.kind === "handout");
  // A file with no size on it must not leave a dangling separator behind.
  const meta = ["PDF", (cfg.size ?? "").trim()].filter(Boolean).join(" · ");
  const label = saved ? "Saved" : "Download";
  const cls = saved ? "w-dl w-saved" : "w-dl";
  return (
    <>
      <div className="w-q" dir="auto">{cfg.title}</div>
      <div className="w-file">
        <span className="w-fileicon" aria-hidden="true" />
        <span className="w-filebody">
          <span className="w-nm">{cfg.file}</span>
          <span className="w-mt w-num">{meta}</span>
        </span>
        {cfg.key ? (
          <a
            className={cls}
            href={assetUrl(cfg.key)}
            download={cfg.file}
            target="_blank"
            rel="noreferrer"
            onClick={() => onAnswer(interaction.id, { kind: "handout", downloaded: true })}
          >
            {label}
          </a>
        ) : (
          <button
            className={cls}
            type="button"
            disabled={saved}
            onClick={() => onAnswer(interaction.id, { kind: "handout", downloaded: true })}
          >
            {label}
          </button>
        )}
      </div>
      {saved ? null : (
        <div className="w-mrow">
          <span />
          {later}
        </div>
      )}
    </>
  );
}
